import { isUnsupportedPublishPlatform } from '@/lib/posts/unsupported-publish';

/**
 * The snapshot is what was ACTUALLY sent for review. The reviewer page renders
 * it and never reads the live source row, so editing a draft after approval
 * cannot silently carry the approval to text nobody approved.
 *
 * `snapshotHash` is compared against a freshly-built snapshot of the live row
 * on the OPERATOR side to surface "Changed since approved". The reviewer is
 * never shown a stale item — they simply see what they were sent.
 */
export interface ApprovalSnapshotOption {
  label: string;
  body: string;
  mediaUrls?: string[];
}

export interface ApprovalSnapshot {
  sourceKind?: 'article' | 'proposal' | 'external_page';
  articleRef?: { messageId: string; documentId: string; contentHash: string };
  platform?: string | null;
  headline?: string | null;
  body?: string | null;
  mediaUrls?: string[];
  mediaType?: 'image' | 'video' | null;
  scheduledAt?: string | null;
  referenceUrl?: string | null;
  /** Present only when the operator deliberately sent a choice (spec D6). */
  options?: ApprovalSnapshotOption[];
  /**
   * Content-pillar framing. Present only on items built from the editorial
   * map (`/reach/editorial`), never on event/plan-slot items — its presence
   * is what tells the reviewer page to group by pillar instead of by date
   * (`reviewGroupStrategy` in `review-grouping.ts`). Carried in the jsonb
   * snapshot rather than a new column: `approval_items` has no pillar FK by
   * design (pillars live in `brand_kits.brand_guidelines`, not a table), and a
   * name here can never orphan the way a stored id could.
   */
  pillarId?: string | null;
  pillarName?: string | null;
  branchName?: string | null;
}

/**
 * Canonical JSON: keys sorted at every level, undefined dropped, so two
 * structurally identical snapshots always hash the same regardless of the
 * order the builder happened to assign properties.
 */
export function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      if (source[key] === undefined) continue;
      out[key] = canonicalize(source[key]);
    }
    return out;
  }
  return value;
}

export function canonicalJson(snapshot: ApprovalSnapshot): string {
  return JSON.stringify(canonicalize(snapshot));
}

/**
 * sha-256 hex over the canonical JSON. `globalThis.crypto.subtle` is present in
 * both the browser and the Node 20 server runtime, so one implementation serves
 * the operator UI, the reviewer page and the server routes — which matters,
 * because a hash computed differently on two sides would report every item as
 * changed.
 */
export async function hashSnapshot(snapshot: ApprovalSnapshot): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(snapshot));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function firstPublishablePlatform(platforms: readonly string[] | null | undefined): string | null {
  const list = platforms ?? [];
  return list.find((platform) => !isUnsupportedPublishPlatform(platform)) ?? list[0] ?? null;
}

/** Build the snapshot for a drafted post. Empty strings normalise to null. */
export function snapshotFromPost(post: {
  content?: string | null;
  title?: string | null;
  platforms?: string[] | null;
  media_urls?: string[] | null;
  media_type?: string | null;
  scheduled_at?: string | null;
}): ApprovalSnapshot {
  const media = (post.media_urls ?? []).filter((url): url is string => Boolean(url));
  return {
    platform: firstPublishablePlatform(post.platforms),
    headline: post.title?.trim() || null,
    body: post.content?.trim() || null,
    mediaUrls: media,
    mediaType: post.media_type === 'video' ? 'video' : media.length ? 'image' : null,
    scheduledAt: post.scheduled_at ?? null,
  };
}

/**
 * A WRITTEN ARTICLE, from the `tool-generate_document` output stored on
 * `ai_messages.parts`.
 *
 * 🔴 WHY THIS HAD TO EXIST. `project_tasks.artifact_kind = 'document'` with a
 * populated `artifact_id` means the article HAS BEEN WRITTEN. But
 * `listApprovalCandidates` only ever resolved `artifact_id -> social_posts`,
 * which always misses for an article (its artifact is an `ai_messages` row, not
 * a post), so the candidate fell through to `snapshotFromPlanSlot` and shipped
 * the TOPIC-STAGE blurb.
 *
 * The result on the real ITC Tax desk: 11 finished articles rendered as cards
 * labelled "Article", asking a client to approve, whose entire visible body was
 * `"Audience: Tax Director; CFO; Data Center Asset Manager"`. A control that is
 * present, correctly labelled, and wrong — the Quality Bar's second law
 * ("nothing may falsely pass") in its purest form, live on a real client.
 *
 * The body is the article's own markdown. The meta title/description are NOT
 * included: they are SEO fields for the operator, and a client asked to approve
 * a piece should read the piece.
 */
export function snapshotFromArticle(input: {
  articleRef?: ApprovalSnapshot['articleRef'];
  markdown?: string | null;
  title?: string | null;
  scheduled_at?: string | null;
}): ApprovalSnapshot {
  const body = input.markdown?.trim() || null;
  return {
    sourceKind: 'article',
    ...(input.articleRef ? { articleRef: input.articleRef } : {}),
    platform: null,
    headline: input.title?.trim() || null,
    body,
    mediaUrls: [],
    mediaType: null,
    scheduledAt: input.scheduled_at ?? null,
  };
}

/**
 * A plan row that has no drafted artifact yet. The reviewer still sees what is
 * planned for that date — this is the row the operator's own sheet carries as
 * "Capture one real observation → a client POV post or saved draft".
 */
export function snapshotFromPlanSlot(slot: {
  topic?: string | null;
  rationale?: string | null;
  cta?: string | null;
  platform?: string | null;
  scheduled_at?: string | null;
}): ApprovalSnapshot {
  const lines = [
    slot.rationale?.trim(),
    slot.cta?.trim() ? `CTA: ${slot.cta.trim()}` : null,
  ].filter(Boolean);
  return {
    platform: slot.platform ?? null,
    headline: slot.topic?.trim() || null,
    body: lines.length ? lines.join('\n\n') : null,
    mediaUrls: [],
    mediaType: null,
    scheduledAt: slot.scheduled_at ?? null,
  };
}

/**
 * The Audience / Reference lines of a topic filed from the editorial sheet.
 * Shared by `snapshotFromEditorialTopic` and the sheet branch of
 * `snapshotFromTask`, so the wording is produced in exactly one place.
 */
function proposalBody(audience?: string | null, reference?: string | null): string | null {
  const lines = [
    audience?.trim() ? `Audience: ${audience.trim()}` : null,
    reference?.trim() ? `Reference: ${reference.trim()}` : null,
  ].filter((line): line is string => Boolean(line));
  return lines.length ? lines.join('\n\n') : null;
}

export interface PillarFraming {
  pillarId?: string | null;
  pillarName?: string | null;
  branchName?: string | null;
}

const PILLAR_FRAMING_KEYS = ['pillarId', 'pillarName', 'branchName'] as const;

/**
 * Add the content-pillar framing to a snapshot WITHOUT touching what it says.
 *
 * 🔴 FRAMING IS METADATA, NEVER CONTENT. Sending a topic through the editorial
 * programme dialog used to REPLACE the snapshot with a different builder's
 * output (an "Audience:" blurb), so the same topic read differently the second
 * time it was sent. Pillar / branch are now layered onto whatever
 * `snapshotFromTask` produced, which keeps the body, CTA, platform and schedule
 * the first send carried.
 */
export function withPillarFraming(
  snapshot: ApprovalSnapshot,
  framing: PillarFraming,
): ApprovalSnapshot {
  return {
    ...snapshot,
    pillarId: framing.pillarId ?? null,
    pillarName: framing.pillarName ?? null,
    branchName: framing.branchName ?? null,
  };
}

/**
 * Copy the pillar framing a frozen snapshot carried onto a freshly built live
 * one, so the two hash on content alone. Keys the frozen snapshot never had are
 * not invented.
 */
export function carryPillarFraming(
  live: ApprovalSnapshot,
  frozen: ApprovalSnapshot | null | undefined,
): ApprovalSnapshot {
  if (!frozen) return live;
  const out: ApprovalSnapshot = { ...live };
  for (const key of PILLAR_FRAMING_KEYS) {
    if (key in frozen) out[key] = frozen[key] ?? null;
  }
  return out;
}

/**
 * A proposed article from the editorial map (`project_tasks` filed under a
 * content pillar). The reviewer sees the topic once, as the card's own title
 * — never repeated in the body — so this carries only what is NOT already the
 * title: who it is for and what it draws on, plus the pillar framing that
 * `ReviewPage` groups by.
 *
 * ⚠️ Not a send path on its own any more: `snapshotFromTask` is. This remains
 * the shape of a topic that really IS a sheet row (it has a reference).
 */
export function snapshotFromEditorialTopic(topic: {
  topic: string;
  audience?: string | null;
  reference?: string | null;
  pillarId?: string | null;
  pillarName?: string | null;
  branchName?: string | null;
}): ApprovalSnapshot {
  return withPillarFraming(
    {
      sourceKind: 'proposal',
      headline: topic.topic.trim() || null,
      body: proposalBody(topic.audience, topic.reference),
      mediaUrls: [],
      mediaType: null,
    },
    topic,
  );
}

/**
 * THE ONE BUILDER FOR WHAT A `project_tasks` ROW SHOWS A REVIEWER.
 *
 * Every send path that carries a `project_task_id` builds its snapshot here, so
 * the same task reads the same on its second send as on its first. Precedence
 * is "the most finished thing the task has":
 *
 *   1. a written article  (`artifact_kind = 'document'`)  -> the article body
 *   2. a drafted post     (`artifact_kind = 'social_post'`) -> the post copy
 *   3. a sheet topic      (has a `summary`, the sheet's Reference column)
 *                         -> "Audience: / Reference:" lines
 *   4. otherwise a plan slot -> rationale + "CTA:" line, platform, schedule
 *
 * 🔴 `rationale` MEANS TWO THINGS, AND THAT WAS THE BUG. On an event plan slot
 * it is the post's rationale and the slot also carries `cta`, `platform` and
 * `scheduled_at`. On an editorial-sheet topic it holds "Primary audience" and
 * `summary` holds the reference. The editorial dialog read only `rationale`
 * and `summary`, labelled every rationale "Audience:", and dropped the CTA,
 * platform and schedule — so re-sending ITC Tax's Sep 4 plan slots on Sep 7
 * replaced a 181-character post body with a 105-character blurb. The sheet
 * branch is keyed on `summary` because no pillar-filed row on ITC Tax that
 * carries a CTA also carries a summary (checked 2026-10-02), while every sheet
 * topic with a reference does.
 */
export interface TaskSnapshotSource {
  topic?: string | null;
  rationale?: string | null;
  cta?: string | null;
  summary?: string | null;
  platform?: string | null;
  scheduled_at?: string | null;
}

export function snapshotFromTask(
  task: TaskSnapshotSource,
  resolved: {
    article?: Parameters<typeof snapshotFromArticle>[0] | null;
    post?: Parameters<typeof snapshotFromPost>[0] | null;
  } = {},
): ApprovalSnapshot {
  if (resolved.article?.markdown?.trim()) return snapshotFromArticle(resolved.article);
  if (resolved.post) return snapshotFromPost(resolved.post);
  if (task.summary?.trim()) {
    return {
      sourceKind: 'proposal',
      headline: task.topic?.trim() || null,
      body: proposalBody(task.rationale, task.summary),
      mediaUrls: [],
      mediaType: null,
    };
  }
  return snapshotFromPlanSlot(task);
}

/**
 * What a reader actually READS: title, headline, body, markdown and the
 * article's content hash. Pillar, branch, platform, schedule and media are
 * metadata that changes between sends without changing what was approved.
 *
 * The desk dedupe (`server/api/desk/_build-payload.ts` `sameWording`) and the
 * operator's "this re-send changes nothing" warning both compare THIS, so the
 * two can never disagree about whether a re-send is the same wording.
 */
export function snapshotWordingParts(
  title: string | null | undefined,
  snapshot: Record<string, unknown> | null | undefined,
): string[] {
  const snap = snapshot ?? {};
  const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
  const articleRef = snap.articleRef;
  const contentHash =
    articleRef && typeof articleRef === 'object'
      ? text((articleRef as Record<string, unknown>).contentHash)
      : '';
  return [text(title), text(snap.headline), text(snap.body), text(snap.markdown), contentHash];
}

export function sameSnapshotWording(
  a: { title: string | null | undefined; snapshot: Record<string, unknown> | null | undefined },
  b: { title: string | null | undefined; snapshot: Record<string, unknown> | null | undefined },
): boolean {
  const left = snapshotWordingParts(a.title, a.snapshot);
  const right = snapshotWordingParts(b.title, b.snapshot);
  return left.every((part, index) => part === right[index]);
}

/**
 * A LIVE WEB PAGE ON THE CLIENT'S OWN SITE — the one snapshot in this file
 * that cannot keep this file's promise, said out loud instead of hidden.
 *
 * Every other builder freezes CONTENT: the reviewer page renders the frozen
 * copy and never the live source row, so editing a draft after approval cannot
 * carry that approval to text nobody approved. A URL cannot do that. The bytes
 * live on the client's own WordPress, which Mora neither hosts nor watches, so
 * the page can change with `snapshot_hash` untouched.
 *
 * 🔴 SO DO NOT BUILD A FAKE DRIFT DETECTOR HERE. The obvious move is to fetch
 * the page at send time, store its title and a `fetchedAt`, and compare later.
 * That was designed and deliberately dropped: it does not restore the
 * guarantee, it relocates the check from "automatic, every load" to "manual,
 * and nothing ever runs it", while LOOKING like diligence. It is the same
 * mistake as printing "4 of 6 sources verified" — see the desk README's
 * "Counting sources", where the copy says "read", never "verified", because
 * fetching a page is a claim about our diligence, not about the page.
 *
 * The honest design is a permanent, uncollapsible caveat on the card —
 * "Mora hasn't checked this page since it was sent" — and a `title` the
 * OPERATOR typed rather than one we scraped. A typed title cannot imply we
 * inspected anything.
 */
export function snapshotFromExternalPage(page: {
  url: string;
  title?: string | null;
  note?: string | null;
}): ApprovalSnapshot {
  return {
    sourceKind: 'external_page',
    referenceUrl: page.url.trim(),
    headline: page.title?.trim() || null,
    // The operator's note to the reviewer, if any — never a fetched summary.
    body: page.note?.trim() || null,
    mediaUrls: [],
    mediaType: null,
  };
}

export function snapshotFromMedia(media: {
  url?: string | null;
  media_type?: string | null;
  title?: string | null;
}): ApprovalSnapshot {
  return {
    headline: media.title?.trim() || null,
    body: null,
    mediaUrls: media.url ? [media.url] : [],
    mediaType: media.media_type === 'video' ? 'video' : 'image',
  };
}
